import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { favoritesAPI } from '../utils/api';

// Функция для показа уведомлений через window
const showToast = (message, type = 'success', duration = 3000) => {
  if (typeof window !== 'undefined' && window.showToastNotification) {
    window.showToastNotification(message, type, duration);
  }
};

const FavoritesContext = createContext();

// Типы действий для reducer
const FAVORITES_ACTIONS = {
  LOAD_FAVORITES: 'LOAD_FAVORITES',
  ADD_FAVORITE: 'ADD_FAVORITE',
  REMOVE_FAVORITE: 'REMOVE_FAVORITE',
  CLEAR_FAVORITES: 'CLEAR_FAVORITES'
};

// Начальное состояние избранного
const initialState = {
  items: [], // Массив товаров из избранного
  productIds: [], // Массив ID товаров для быстрой проверки
  loading: false,
  error: null
};

// Reducer для управления состоянием избранного
const favoritesReducer = (state, action) => {
  switch (action.type) {
    case FAVORITES_ACTIONS.LOAD_FAVORITES: {
      const items = action.payload || [];
      // Извлекаем product_id из каждого элемента избранного
      const productIds = items.map(item => {
        // Если это объект с product_id (из JOIN запроса), используем его
        return item.product_id || item.id;
      });
      return {
        ...state,
        items,
        productIds,
        loading: false,
        error: null
      };
    }
    
    case FAVORITES_ACTIONS.ADD_FAVORITE: {
      const product = action.payload;
      const productId = product.product_id || product.id;
      
      // Проверяем, нет ли уже этого товара
      if (state.productIds.includes(productId)) {
        return state;
      }
      
      return {
        ...state,
        items: [...state.items, product],
        productIds: [...state.productIds, productId],
        error: null
      };
    }
    
    case FAVORITES_ACTIONS.REMOVE_FAVORITE: {
      const productId = action.payload;
      return {
        ...state,
        items: state.items.filter(item => (item.product_id || item.id) !== productId),
        productIds: state.productIds.filter(id => id !== productId),
        error: null
      };
    }
    
    case FAVORITES_ACTIONS.CLEAR_FAVORITES: {
      return initialState;
    }
    
    default:
      return state;
  }
};

// Провайдер контекста избранного
export const FavoritesProvider = ({ children }) => {
  const [state, dispatch] = useReducer(favoritesReducer, initialState);
  const { user, isAuthenticated } = useAuth();
  
  // Функция для загрузки избранного с сервера
  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated() || !user) {
      return;
    }
    
    try {
      dispatch({ type: FAVORITES_ACTIONS.LOAD_FAVORITES, payload: [] });
      const response = await favoritesAPI.getAll();
      
      if (response.data && response.data.favorites) {
        // Данные уже содержат полную информацию о товарах из JOIN запроса
        dispatch({ type: FAVORITES_ACTIONS.LOAD_FAVORITES, payload: response.data.favorites });
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      dispatch({ type: FAVORITES_ACTIONS.LOAD_FAVORITES, payload: [] });
    }
  }, [isAuthenticated, user, dispatch]);

  // Загружаем избранное при инициализации или смене пользователя
  useEffect(() => {
    if (isAuthenticated() && user) {
      loadFavorites();
    } else {
      // Очищаем избранное при выходе
      dispatch({ type: FAVORITES_ACTIONS.CLEAR_FAVORITES });
    }
  }, [user, isAuthenticated, loadFavorites, dispatch]);
  
  // Функция для добавления товара в избранное
  const addToFavorites = async (productId) => {
    if (!isAuthenticated() || !user) {
      throw new Error('Необходима авторизация для добавления в избранное');
    }
    
    try {
      await favoritesAPI.add(productId);
      
      // Получаем данные товара (можно оптимизировать, передавая product напрямую)
      // Пока просто добавляем ID, полные данные загрузятся при следующей загрузке
      dispatch({ 
        type: FAVORITES_ACTIONS.ADD_FAVORITE, 
        payload: { product_id: productId, id: productId } 
      });
      
      // Перезагружаем избранное для получения полных данных
      await loadFavorites();
      
      // Показываем уведомление об успехе
      showToast('Товар добавлен в избранное', 'success');
    } catch (error) {
      console.error('Error adding to favorites:', error);
      showToast(error.message || 'Ошибка при добавлении в избранное', 'error');
      throw error;
    }
  };
  
  // Функция для удаления товара из избранного
  const removeFromFavorites = async (productId) => {
    if (!isAuthenticated() || !user) {
      throw new Error('Необходима авторизация для удаления из избранного');
    }
    
    try {
      await favoritesAPI.remove(productId);
      dispatch({ type: FAVORITES_ACTIONS.REMOVE_FAVORITE, payload: productId });
      
      // Показываем уведомление об успехе
      showToast('Товар удален из избранного', 'success');
    } catch (error) {
      console.error('Error removing from favorites:', error);
      showToast(error.message || 'Ошибка при удалении из избранного', 'error');
      throw error;
    }
  };
  
  // Функция для проверки, есть ли товар в избранном
  const isFavorite = (productId) => {
    return state.productIds.includes(productId);
  };
  
  // Функция для получения количества товаров в избранном
  const getFavoritesCount = () => {
    return state.items.length;
  };
  
  const value = {
    // Состояние
    items: state.items,
    productIds: state.productIds,
    loading: state.loading,
    error: state.error,
    
    // Функции
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    loadFavorites,
    getFavoritesCount
  };
  
  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

// Hook для использования контекста избранного
export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export default FavoritesContext;

